import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search,
  Calendar as CalendarIcon,
  MapPin,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  Sun,
  Cloud,
  RotateCcw,
  X,
  ArrowLeft,
  Clock,
  Zap,
} from 'lucide-react';
import {
  agendamentoAPI,
  brandingAPI,
  resolveAssetUrl,
  type Agendamento,
  type AgendamentoPeriodo,
} from '../../services/api';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Página PÚBLICA de agendamento (/agendar) — mobile-first com fluxo em modais.
 *
 * Substitui o `BuscaCpfCnpj.js` legado. Fluxo:
 *
 *   1. Hero card (sempre visível): brand + logo + input CPF/CNPJ
 *   2. Após busca → modal full-screen "Contratos" (lista de contratos)
 *   3. Após selecionar → modal full-screen "Agendar" (calendário + período)
 *   4. Sucesso → modal full-screen confirmação + protocolo
 *
 * Modais aproveitam toda altura em mobile; em desktop ficam centrados com
 * backdrop. Cada modal tem botão de voltar/fechar pra revisão fácil.
 *
 * Sem auth de SaasUser — backend protege via rate limit.
 */

// ============================================================
// Types
// ============================================================

interface SgpContrato {
  contratoId: number;
  razaoSocial: string;
  contratoStatusDisplay: string;
  servico_plano?: string;
  endereco_logradouro?: string;
  endereco_numero?: string;
  endereco_complemento?: string;
  endereco_bairro?: string;
  endereco_cidade?: string;
  tags?: Array<{ tag: string }>;
}

interface SgpResult {
  contratos?: SgpContrato[];
  msg?: string;
}

// ============================================================
// Helpers
// ============================================================

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}
function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function startOfDay(d: Date) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}
function formatBR(d: Date) {
  return d.toLocaleDateString('pt-BR');
}
function formatISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/**
 * Para o backend, enviamos a data com horario fixo de meio-dia UTC.
 * Evita o classico bug "agendou dia 30, exibiu dia 29": new Date("2026-12-30")
 * vira midnight UTC e, exibido em BRT (UTC-3), volta um dia. Usando 12:00 UTC,
 * a data continua sendo dia 30 em qualquer timezone entre UTC-11 e UTC+11.
 */
function dateToApiISO(d: Date) {
  return `${formatISO(d)}T12:00:00.000Z`;
}
function monthLabel(d: Date) {
  const s = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function maskCpfCnpj(raw: string) {
  const d = raw.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return d
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

// ============================================================
// Modal genérico — full-screen em mobile, centrado em desktop
// ============================================================

function Modal({
  open,
  title,
  onBack,
  onClose,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onBack?: () => void;
  onClose?: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  // Bloqueia scroll do body enquanto aberto
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc fecha; clique fora NÃO fecha (Fase 30 — evita perder o fluxo)
  useModalKeys(() => onClose?.(), undefined, open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/40 sm:items-center sm:p-4">
      <div className="flex w-full max-w-2xl flex-col bg-white shadow-2xl sm:max-h-[92vh] sm:rounded-2xl">
        <header className="sticky top-0 flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-3 sm:rounded-t-2xl">
          {onBack && (
            <button
              onClick={onBack}
              className="rounded-lg p-2 -ml-2 text-slate-600 hover:bg-slate-100"
              aria-label="Voltar"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <h2 className="flex-1 truncate text-base font-bold text-slate-900">{title}</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="rounded-lg p-2 -mr-2 text-slate-600 hover:bg-slate-100"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          )}
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer && (
          <footer className="sticky bottom-0 border-t border-slate-200 bg-white px-4 py-3 sm:rounded-b-2xl">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Page
// ============================================================

export default function AgendarInstalacao() {
  const [brand, setBrand] = useState({ clientName: 'Agendamento', logoUrl: null as string | null });

  // Busca
  const [documento, setDocumento] = useState('');
  const [carregandoBusca, setCarregandoBusca] = useState(false);
  const [erroBusca, setErroBusca] = useState<string | null>(null);
  const [contratos, setContratos] = useState<SgpContrato[]>([]);
  const [contratosModalOpen, setContratosModalOpen] = useState(false);

  // Seleção / calendário
  const [contratoSelecionado, setContratoSelecionado] = useState<SgpContrato | null>(null);
  const [agendamentoExistente, setAgendamentoExistente] = useState<
    (Agendamento & { id: string }) | null
  >(null);
  const [checandoExistente, setChecandoExistente] = useState(false);
  const [agendarModalOpen, setAgendarModalOpen] = useState(false);

  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [vagasPorData, setVagasPorData] = useState<
    Record<string, { manha: number | null; tarde: number | null; bloqueado?: boolean }>
  >({});
  // Encaixe (mesma regra do app prospect): pede vaga além do limite e fica
  // aguardando aprovação do provedor. Dia BLOQUEADO continua indisponível.
  const [modoEncaixe, setModoEncaixe] = useState(false);
  const [encaixePendente, setEncaixePendente] = useState<{
    data: string;
    periodo: AgendamentoPeriodo;
  } | null>(null);
  const [carregandoVagas, setCarregandoVagas] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedPeriodo, setSelectedPeriodo] = useState<AgendamentoPeriodo | null>(null);

  // Submit
  const [submitting, setSubmitting] = useState(false);
  const [resultado, setResultado] = useState<Agendamento | null>(null);
  const resultadoEhEncaixe = resultado?.status === 'pendente_encaixe';
  const [erroSubmit, setErroSubmit] = useState<string | null>(null);

  // Branding
  useEffect(() => {
    brandingAPI
      .get()
      .then((r) => setBrand({ clientName: r.clientName, logoUrl: r.logoUrl }))
      .catch(() => undefined);
  }, []);

  const logoSrc = resolveAssetUrl(brand.logoUrl);
  const documentoLimpo = documento.replace(/\D/g, '');
  const tipoDoc = documentoLimpo.length <= 11 ? 'CPF' : 'CNPJ';
  const docValido = documentoLimpo.length === 11 || documentoLimpo.length === 14;

  // ------ Busca ------
  async function handleBuscar() {
    setContratos([]);
    setContratoSelecionado(null);
    setAgendamentoExistente(null);
    setErroBusca(null);
    setResultado(null);

    if (!docValido) {
      setErroBusca('CPF deve ter 11 dígitos ou CNPJ 14 dígitos.');
      return;
    }

    setCarregandoBusca(true);
    try {
      const data: SgpResult = await agendamentoAPI.public.buscarContrato(documentoLimpo);
      if (data.contratos && data.contratos.length > 0) {
        setContratos(data.contratos);
        setContratosModalOpen(true);
      } else {
        setErroBusca(data.msg ?? 'Nenhum contrato encontrado para este documento.');
      }
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setErroBusca(e.response?.data?.message ?? e.message ?? 'Erro ao consultar ERP.');
    } finally {
      setCarregandoBusca(false);
    }
  }

  // ------ Selecionar contrato ------
  async function selecionarContrato(c: SgpContrato) {
    if (c.contratoStatusDisplay !== 'Inativo') {
      alert('Apenas contratos com status "Inativo" precisam de agendamento de instalação.');
      return;
    }
    const tag = c.tags && c.tags.length > 0 ? c.tags[0]!.tag : '';
    if (tag !== 'CONTRATO ASSINADO') {
      alert(
        'Contrato ainda não assinado. Aguarde a assinatura ou entre em contato com o vendedor.',
      );
      return;
    }

    setContratoSelecionado(c);
    setSelectedDate(null);
    setSelectedPeriodo(null);
    setModoEncaixe(false);
    setEncaixePendente(null);
    setResultado(null);
    setErroSubmit(null);
    setContratosModalOpen(false);
    setAgendarModalOpen(true);

    setChecandoExistente(true);
    try {
      const r = await agendamentoAPI.public.agendamentoAtivo(documentoLimpo, c.contratoId);
      setAgendamentoExistente(r.agendamento);
      setEncaixePendente(r.encaixePendente ?? null);
    } catch {
      setAgendamentoExistente(null);
      setEncaixePendente(null);
    } finally {
      setChecandoExistente(false);
    }
  }

  // ------ Vagas do mês ------
  useEffect(() => {
    if (!contratoSelecionado?.endereco_cidade || !agendarModalOpen) return;
    const cidade = contratoSelecionado.endereco_cidade;
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    setCarregandoVagas(true);
    agendamentoAPI.public
      .vagasNoMes(cidade, formatISO(start), formatISO(end))
      .then((r) => setVagasPorData(r.vagasPorData ?? {}))
      .catch(() => setVagasPorData({}))
      .finally(() => setCarregandoVagas(false));
  }, [contratoSelecionado, currentMonth, agendarModalOpen]);

  // ------ Cálculos calendário ------
  const monthDays = useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(currentMonth);
    const days: Date[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(new Date(d));
    }
    return days;
  }, [currentMonth]);

  const today = startOfDay(new Date());
  const isAfterBusinessHours = new Date().getHours() >= 16;
  const earliestDate = new Date(today);
  earliestDate.setDate(today.getDate() + (isAfterBusinessHours ? 2 : 1));

  const isDayDisabled = useCallback(
    (day: Date): boolean => {
      const dow = day.getDay();
      // Domingo nunca; sábado só aceita pedido de encaixe
      if (dow === 0) return true;
      if (dow === 6 && !modoEncaixe) return true;
      if (day < earliestDate) return true;
      const v = vagasPorData[formatISO(day)];
      if (v?.bloqueado) return true;
      // No encaixe, dia esgotado continua selecionável — é o ponto do pedido
      if (!modoEncaixe && v && v.manha === 0 && v.tarde === 0) return true;
      return false;
    },
    [vagasPorData, earliestDate, modoEncaixe],
  );

  function vagasDoDia(day: Date) {
    return vagasPorData[formatISO(day)] ?? { manha: null, tarde: null };
  }

  // ------ Submit ------
  async function handleAgendar() {
    if (!contratoSelecionado || !selectedDate || !selectedPeriodo) return;
    setSubmitting(true);
    setErroSubmit(null);
    try {
      let ag: Agendamento;
      if (modoEncaixe) {
        // O backend converte o agendamento existente, se houver (e restaura
        // se o encaixe for reprovado)
        ag = await agendamentoAPI.public.criar({
          cpfcnpj: documentoLimpo,
          contratoErpId: contratoSelecionado.contratoId,
          data: dateToApiISO(selectedDate),
          periodo: selectedPeriodo,
          nomeCliente: contratoSelecionado.razaoSocial,
          plano: contratoSelecionado.servico_plano ?? '',
          cidade: contratoSelecionado.endereco_cidade ?? '',
          bairro: contratoSelecionado.endereco_bairro,
          isSqueeze: true,
        });
      } else if (agendamentoExistente) {
        ag = await agendamentoAPI.public.reagendar(agendamentoExistente.id, {
          cpfcnpj: documentoLimpo,
          contratoErpId: contratoSelecionado.contratoId,
          data: dateToApiISO(selectedDate),
          periodo: selectedPeriodo,
        });
      } else {
        ag = await agendamentoAPI.public.criar({
          cpfcnpj: documentoLimpo,
          contratoErpId: contratoSelecionado.contratoId,
          data: dateToApiISO(selectedDate),
          periodo: selectedPeriodo,
          nomeCliente: contratoSelecionado.razaoSocial,
          plano: contratoSelecionado.servico_plano ?? '',
          cidade: contratoSelecionado.endereco_cidade ?? '',
          bairro: contratoSelecionado.endereco_bairro,
        });
      }
      setResultado(ag);
      setAgendarModalOpen(false);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setErroSubmit(e.response?.data?.message ?? e.message ?? 'Erro ao agendar.');
    } finally {
      setSubmitting(false);
    }
  }

  function resetTudo() {
    setDocumento('');
    setContratos([]);
    setContratoSelecionado(null);
    setAgendamentoExistente(null);
    setSelectedDate(null);
    setSelectedPeriodo(null);
    setResultado(null);
    setErroBusca(null);
    setErroSubmit(null);
    setContratosModalOpen(false);
    setAgendarModalOpen(false);
    setModoEncaixe(false);
    setEncaixePendente(null);
  }

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header brand (texto apenas — logo grande fica no hero) */}
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-3">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={brand.clientName}
              className="h-10 w-auto max-w-[3rem] object-contain"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700">
              <CalendarIcon size={20} />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="truncate text-base font-bold text-slate-900 sm:text-lg">
              {brand.clientName}
            </h1>
            <p className="text-xs text-slate-500">Agendamento de instalação</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-6 sm:py-10">
        {/* Logo grande centralizado acima do CPF */}
        {logoSrc && (
          <div className="mb-5 flex justify-center sm:mb-7">
            <img
              src={logoSrc}
              alt={brand.clientName}
              className="h-24 w-auto max-w-[60%] object-contain sm:h-28"
            />
          </div>
        )}

        {/* Hero */}
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-7">
          <h2 className="mb-1 text-lg font-bold text-slate-900 sm:text-xl">
            Agende sua instalação
          </h2>
          <p className="mb-5 text-sm text-slate-600">
            Informe seu CPF ou CNPJ para localizar o contrato e escolher data e período.
          </p>

          <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            CPF ou CNPJ
          </label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={documento}
                onChange={(e) => setDocumento(maskCpfCnpj(e.target.value))}
                onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
                placeholder="000.000.000-00"
                disabled={carregandoBusca}
                className="w-full rounded-lg border border-slate-300 py-3 pl-10 pr-3 text-base focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
              />
            </div>
            <button
              onClick={handleBuscar}
              disabled={carregandoBusca || !docValido}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {carregandoBusca ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Search size={16} />
              )}
              {carregandoBusca ? 'Buscando…' : 'Buscar'}
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {documento ? `Tipo detectado: ${tipoDoc}` : 'Digite somente números'}
          </p>

          {erroBusca && (
            <div className="mt-4 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{erroBusca}</span>
            </div>
          )}
        </section>

        {/* CTA reabrir modal de contratos se já tem resultado */}
        {contratos.length > 0 && !contratosModalOpen && !agendarModalOpen && !resultado && (
          <button
            onClick={() => setContratosModalOpen(true)}
            className="mt-4 w-full rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-300"
          >
            <p className="text-sm font-semibold text-slate-900">
              {contratos.length} contrato(s) encontrado(s) — toque para selecionar
            </p>
          </button>
        )}

        <footer className="mt-8 text-center text-xs text-slate-400">
          {brand.clientName} · Agendamento online
        </footer>
      </main>

      {/* ============================================ */}
      {/*  MODAL — Lista de contratos                  */}
      {/* ============================================ */}
      <Modal
        open={contratosModalOpen}
        title={`${contratos.length} contrato(s) encontrado(s)`}
        onClose={() => setContratosModalOpen(false)}
      >
        <div className="space-y-3">
          {contratos.map((c) => {
            const tag = c.tags && c.tags.length > 0 ? c.tags[0]!.tag : '';
            const isInativo = c.contratoStatusDisplay === 'Inativo';
            const isAssinado = tag === 'CONTRATO ASSINADO';
            const podeAgendar = isInativo && isAssinado;
            return (
              <button
                key={c.contratoId}
                onClick={() => selecionarContrato(c)}
                disabled={!podeAgendar}
                className={`block w-full rounded-xl border p-4 text-left transition ${
                  podeAgendar
                    ? 'border-slate-200 bg-white hover:border-blue-400 hover:shadow active:scale-[0.99]'
                    : 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-70'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-900">
                      {c.razaoSocial}
                    </p>
                    <p className="text-xs text-slate-500">
                      Contrato #{c.contratoId} · {c.contratoStatusDisplay}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                      !isInativo
                        ? 'bg-slate-200 text-slate-600'
                        : isAssinado
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {!isInativo ? 'Já ativo' : isAssinado ? 'Assinado' : 'Não assinado'}
                  </span>
                </div>
                {c.servico_plano && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-600">
                    <FileText size={12} /> {c.servico_plano}
                  </p>
                )}
                <p className="mt-1 flex items-start gap-1.5 text-xs text-slate-600">
                  <MapPin size={12} className="mt-0.5 shrink-0" />
                  <span>
                    {c.endereco_logradouro}, {c.endereco_numero}
                    {c.endereco_complemento ? `, ${c.endereco_complemento}` : ''} ·{' '}
                    {c.endereco_bairro}, {c.endereco_cidade}
                  </span>
                </p>
              </button>
            );
          })}
        </div>
      </Modal>

      {/* ============================================ */}
      {/*  MODAL — Agendar (calendário + período)      */}
      {/* ============================================ */}
      <Modal
        open={agendarModalOpen}
        title={agendamentoExistente ? 'Reagendar instalação' : 'Agendar instalação'}
        onBack={() => {
          setAgendarModalOpen(false);
          if (contratos.length > 0) setContratosModalOpen(true);
        }}
        onClose={() => setAgendarModalOpen(false)}
        footer={
          encaixePendente ? undefined : (
            <button
              onClick={handleAgendar}
              disabled={!selectedDate || !selectedPeriodo || submitting}
              className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold text-white shadow-sm active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${
                modoEncaixe
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting
                ? 'Enviando…'
                : selectedDate && selectedPeriodo
                  ? modoEncaixe
                    ? `Solicitar encaixe para ${formatBR(selectedDate)}`
                    : agendamentoExistente
                      ? `Reagendar para ${formatBR(selectedDate)}`
                      : `Confirmar para ${formatBR(selectedDate)}`
                  : 'Selecione data e período'}
            </button>
          )
        }
      >
        {contratoSelecionado && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="truncate text-sm font-semibold text-slate-900">
              {contratoSelecionado.razaoSocial}
            </p>
            <p className="text-xs text-slate-500">
              Contrato #{contratoSelecionado.contratoId} ·{' '}
              {contratoSelecionado.endereco_cidade}
            </p>
          </div>
        )}

        {checandoExistente && (
          <p className="mb-3 text-xs text-slate-500">Verificando agendamento existente…</p>
        )}

        {agendamentoExistente && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Agendamento existente</p>
              <p className="text-xs">
                {formatBR(new Date(agendamentoExistente.data))} ·{' '}
                {agendamentoExistente.periodo === 'manha' ? 'Manhã' : 'Tarde'}
                {' — '}escolha uma nova data abaixo para reagendar.
              </p>
            </div>
          </div>
        )}

        {encaixePendente ? (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <Clock size={18} className="mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Solicitação de encaixe em análise</p>
              <p className="mt-1 text-xs leading-relaxed">
                Você pediu encaixe para{' '}
                <strong>
                  {formatBR(new Date(encaixePendente.data))} ·{' '}
                  {encaixePendente.periodo === 'manha' ? 'Manhã' : 'Tarde'}
                </strong>
                . Nossa equipe está avaliando e entrará em contato para confirmar.
              </p>
            </div>
          </div>
        ) : (
          <>
        {/* Encaixe — pedir vaga além do limite */}
        <label
          className={`mb-4 flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition ${
            modoEncaixe
              ? 'border-amber-300 bg-amber-50'
              : 'border-slate-200 bg-white hover:border-amber-200'
          }`}
        >
          <input
            type="checkbox"
            checked={modoEncaixe}
            onChange={(e) => {
              setModoEncaixe(e.target.checked);
              setSelectedDate(null);
              setSelectedPeriodo(null);
              setErroSubmit(null);
            }}
            className="mt-0.5 h-4 w-4 shrink-0 accent-amber-600"
          />
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              <Zap size={14} className="text-amber-600" />
              Não encontrou vaga? Solicitar encaixe
            </span>
            <span className="mt-0.5 block text-xs leading-relaxed text-slate-600">
              {modoEncaixe
                ? 'Dias esgotados e sábados ficam liberados. O encaixe não é garantido: fica em análise e nossa equipe confirma com você.'
                : 'Pede um horário mesmo sem vaga disponível, inclusive aos sábados. Depende de aprovação da equipe.'}
            </span>
          </span>
        </label>

        {/* Calendário */}
        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <header className="mb-3 flex items-center justify-between">
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
              aria-label="Mês anterior"
            >
              <ChevronLeft size={20} />
            </button>
            <h3 className="text-sm font-semibold text-slate-900">{monthLabel(currentMonth)}</h3>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
              aria-label="Próximo mês"
            >
              <ChevronRight size={20} />
            </button>
          </header>

          {carregandoVagas && (
            <p className="mb-2 text-center text-xs text-slate-500">
              <Loader2 size={12} className="mr-1 inline animate-spin" />
              Carregando vagas…
            </p>
          )}

          <div className="grid grid-cols-7 gap-1 text-center text-xs">
            {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((d) => (
              <div key={d} className="py-1 font-semibold text-slate-500">
                {d}
              </div>
            ))}
            {Array.from({ length: monthDays[0]?.getDay() ?? 0 }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {monthDays.map((day) => {
              const disabled = isDayDisabled(day);
              const selected = selectedDate && isSameDay(selectedDate, day);
              const v = vagasPorData[formatISO(day)];
              const semVagas =
                !modoEncaixe && v && v.manha === 0 && v.tarde === 0 && day >= earliestDate;
              return (
                <button
                  key={day.toISOString()}
                  disabled={disabled}
                  onClick={() => {
                    setSelectedDate(day);
                    setSelectedPeriodo(null);
                  }}
                  className={`aspect-square rounded-lg text-sm font-medium transition ${
                    selected
                      ? 'bg-blue-600 text-white shadow-sm'
                      : disabled
                        ? semVagas
                          ? 'cursor-not-allowed bg-red-50 text-red-300 line-through'
                          : 'cursor-not-allowed text-slate-300'
                        : 'text-slate-700 hover:bg-blue-50 hover:text-blue-700 active:bg-blue-100'
                  }`}
                  title={v?.bloqueado ? 'Agenda bloqueada' : semVagas ? 'Sem vagas' : undefined}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          <p className="mt-2 text-center text-[11px] text-slate-500">
            A partir de <strong>{formatBR(earliestDate)}</strong> ·{' '}
            {modoEncaixe ? 'Sábado só por encaixe · Domingo indisponível' : 'Sáb/dom indisponíveis'}
          </p>
        </div>

        {/* Período */}
        {selectedDate && (
          <div className="mt-4">
            <p className="mb-2 text-sm font-semibold text-slate-700">
              Período em {formatBR(selectedDate)}
            </p>
            <div className="grid grid-cols-2 gap-3">
              {(['manha', 'tarde'] as const).map((p) => {
                const v = vagasDoDia(selectedDate);
                const restantes = p === 'manha' ? v.manha : v.tarde;
                const esgotado = restantes === 0;
                // No encaixe o período esgotado é justamente o que se pede
                const sem = esgotado && !modoEncaixe;
                const isSel = selectedPeriodo === p;
                const Icon = p === 'manha' ? Sun : Cloud;
                return (
                  <button
                    key={p}
                    disabled={sem}
                    onClick={() => setSelectedPeriodo(p)}
                    className={`flex flex-col items-center gap-1 rounded-xl border-2 p-4 transition active:scale-[0.98] ${
                      isSel
                        ? modoEncaixe
                          ? 'border-amber-500 bg-amber-50 text-amber-800 shadow-sm'
                          : 'border-blue-600 bg-blue-50 text-blue-700 shadow-sm'
                        : sem
                          ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                          : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50/40'
                    }`}
                  >
                    <Icon size={22} />
                    <span className="text-sm font-semibold">
                      {p === 'manha' ? 'Manhã' : 'Tarde'}
                    </span>
                    <span className="text-[11px] text-slate-500">
                      {restantes === null
                        ? 'Vagas livres'
                        : esgotado
                          ? modoEncaixe
                            ? 'Esgotado — pedir encaixe'
                            : 'Sem vagas'
                          : `${restantes} vaga${restantes === 1 ? '' : 's'}`}
                    </span>
                  </button>
                );
              })}
            </div>

            {erroSubmit && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{erroSubmit}</span>
              </div>
            )}
          </div>
        )}
          </>
        )}
      </Modal>

      {/* ============================================ */}
      {/*  MODAL — Sucesso                              */}
      {/* ============================================ */}
      <Modal
        open={!!resultado}
        title={resultadoEhEncaixe ? 'Encaixe solicitado' : 'Agendamento confirmado'}
      >
        {resultado && (
          <div className="text-center">
            {resultadoEhEncaixe ? (
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <Clock size={34} />
              </div>
            ) : (
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <CheckCircle2 size={36} />
              </div>
            )}
            <h2 className="text-lg font-bold text-slate-900">
              {resultadoEhEncaixe
                ? 'Solicitação de encaixe enviada'
                : agendamentoExistente
                  ? 'Reagendamento confirmado!'
                  : 'Agendamento confirmado!'}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {resultadoEhEncaixe
                ? 'Seu pedido ainda não está confirmado. Nossa equipe vai avaliar a disponibilidade e entrar em contato.'
                : 'Anote o protocolo. Nossa equipe entrará em contato próximo da data.'}
            </p>
            {resultadoEhEncaixe && agendamentoExistente && (
              <p className="mx-auto mt-2 max-w-md text-xs text-slate-500">
                Se o encaixe não for aprovado, seu agendamento atual (
                {formatBR(new Date(agendamentoExistente.data))}) continua valendo.
              </p>
            )}

            <dl className="mx-auto mt-6 grid max-w-md grid-cols-1 gap-3 text-left text-sm sm:grid-cols-2">
              <Info label="Cliente" value={resultado.nomeCliente} />
              <Info label="Contrato" value={`#${resultado.contratoErpId}`} />
              <Info label="Data" value={formatBR(new Date(resultado.data))} />
              <Info label="Período" value={resultado.periodo === 'manha' ? 'Manhã' : 'Tarde'} />
              <Info label="Cidade" value={resultado.cidade} />
              {resultado.protocoloUra && (
                <Info label="Protocolo" value={resultado.protocoloUra} highlight />
              )}
            </dl>

            <button
              onClick={resetTudo}
              className="mt-8 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800"
            >
              <RotateCcw size={14} />
              Novo agendamento
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}

function Info({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | number;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        highlight ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-slate-50'
      }`}
    >
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd
        className={`mt-0.5 font-semibold ${
          highlight ? 'text-emerald-800' : 'text-slate-900'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
